import { DefaultRoute, DefaultRouteAdmin, DefaultRouteSuperAdmin, DefaultRouteUser, DefaultRoutePartner } from "../router/routes"

// ** Third Party Components
import { format } from "date-fns"

// ** Custom Hooks
import useJwt from "@src/auth/jwt/useJwt"
const config = useJwt.jwtConfig

// ** Axios Imports
import axios from "axios"

import { INTERNATIONALIZATION, CAPACITATION_AND_CERTIFICATIONS, COMPETITIVENESS, DIGITAL } from '../constants'

export const verifyLoginDataUser = () => {
  const userData = JSON.parse(localStorage.getItem("userData"))
  if (userData) {
    const { accessToken, role } = userData
    console.log(`USERDATA`, userData)
    let path = 'user'
    switch (role) {
      case 'company':
        path = 'company'
        break
      case 'partner':
        path = 'partner'
        break
    }
    // GET ROLE AND EXECUTE RESPECTIVE FUNCTION   
    axios
      .get(`logins/${path}/${accessToken}`)
      .then((response) => {
        if (response.data && !response.data.exists) {
          localStorage.removeItem("userData")
          localStorage.removeItem(config.storageTokenKeyName)
          localStorage.removeItem(config.storageRefreshTokenKeyName)
          window.location.href = '/login'
        }
      })
      .catch((error) => console.log(error))
  }
}

export const dateConverter = (date, formatter = "dd/MM/yyyy H:m") => {
  if (!date) return false
  const dateParsed = new Date(date)
  return format(dateParsed, formatter)
}

// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = (obj) => Object.keys(obj).length === 0

// ** Returns K format from a number
export const kFormatter = (num) => (num > 999 ? `${(num / 1000).toFixed(1)}k` : num)

// ** Converts HTML to string
export const htmlToString = (html) => html.replace(/<\/?[^>]+(>|$)/g, "")

// ** Checks if the passed date is today
const isToday = (date) => {
  const today = new Date()
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  )
}

/**
 ** Format and return date in Humanize format
 ** Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 ** Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */
export const formatDate = (
  value,
  formatting = { month: "short", day: "numeric", year: "numeric" }
) => {
  if (!value) return value
  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value))
}

// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
  const date = new Date(value)
  let formatting = { month: "short", day: "numeric", year: "numeric" }

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: "numeric", minute: "numeric" }
  }

  return new Intl.DateTimeFormat("es-ES", formatting).format(new Date(value))
}

/**
 ** Return if user is logged in
 ** This is completely up to you and how you want to store the token in your frontend application
 *  ? e.g. If you are using cookies to store the application please update this function
 */
export const isUserLoggedIn = () => localStorage.getItem("userData")
export const getUserData = () => JSON.parse(localStorage.getItem("userData"))

/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note role field is just for showing purpose it's not used by anything in frontend
 ** We are checking role just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = (userRole) => {
  const userData = getUserData()
  if (userData.isDefaultPassword) return "/reset-password"
  if (userRole === "company") return DefaultRoute
  if (userRole === "admin") return DefaultRouteAdmin
  if (userRole === "superadmin") return DefaultRouteSuperAdmin
  if (userRole === "user") return DefaultRouteUser
  if (userRole === "partner") return DefaultRoutePartner
  return "/login"
}

// ** React Select Theme Colors
export const selectThemeColors = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: "#7367f01a", // for option hover bg-color
    primary: "#7367f0", // for selected option bg-color
    neutral10: "#7367f0", // for tags bg-color
    neutral20: "#ededed", // for input border-color
    neutral30: "#ededed" // for input hover border-color
  }
})

/**
 * jsonToCsv
 * Handler to convert json data to csv format by commas
 * @param {array} headers {text,value} headers file
 * @param {array} jsonData data to convert
 */
export const jsonToCsv = async (headers, jsonData = []) => {
  try {
    const bodyWithRowsCSV = []
    const headCSV = headers.map(header => header.text)
    const nameProperties = headers.map(header => header.value)
    const bodyCSV = [[headCSV]]

    for (let i = 0; i < jsonData.length; i += 1) {
      const dataRow = []
      for (let k = 0; k < nameProperties.length; k += 1) {
        const word = jsonData[i][nameProperties[k]]
          ? jsonData[i][nameProperties[k]].toString()
          : 'N/A'
        let cleanWord = word ? word.replace(/,/g, ' ') : ''
        cleanWord = cleanWord.replace(/(\r\n|\n|\r)/gm, ' ')
        dataRow.push(cleanWord.trim() || 'N/A')
      }
      bodyCSV.push(dataRow)
    }

    for (let j = 0; j < bodyCSV.length; j += 1) {
      bodyWithRowsCSV.push(bodyCSV[j].join(','))
    }

    const bodyCSVString = bodyWithRowsCSV.join('\r\n')
    return bodyCSVString
  } catch (error) {
    throw new Error(error)
  }
}

/**
 * downloadCSV
 * Function helper to download blob file
 * @param {object} blob Blob file converted
 * @param {string} filename file name with extension
 */
export const downloadCSV = async (data, filename) => {
  try {
    const BOM = '\uFEFF'
    const csvData = new Blob([`${BOM}${data}`], { type: 'text/csv;charset=utf-8' })
    const blobUrl = URL.createObjectURL(csvData)

    // Create a link element
    const link = document.createElement('a')

    // Set link's href to point to the Blob URL
    link.href = blobUrl
    link.download = filename

    // Append link to the body
    document.body.appendChild(link)

    // Dispatch click event on the link
    // This is necessary as link.click() does not work on the latest firefox
    link.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      })
    )

    // Remove link from body
    document.body.removeChild(link)
    return true
  } catch (error) {
    throw new Error(error)
  }
}

/**
 * Function helper to validate if variable is and object and it is empty
 * @param {object} value value to validate
 * @returns boolean
 */
export const isEmptyObject = value => !value || typeof value !== 'object' || (typeof value === 'object' && !Object.keys(value).length)

/**
 * Function helper to validate if field is array and it is empty
 * @param {array} value value to validate
 * @returns boolean
 */
export const isEmptyArray = value => !value || (Array.isArray(value) && !value.length)

export const formatClasification = (area, year) => {
  let value = ''
  switch (area) {
    case INTERNATIONALIZATION:
      value = `CFCE.0/3.2/1.0`
      break
    case CAPACITATION_AND_CERTIFICATIONS:
      value = `CFCE.0/2.3/3.0/${year}`
      break
    case COMPETITIVENESS:
      value = `CFCE.0/4.1/1.1/${year} A`
      break
    case DIGITAL:
      value = `CFCE.0/4.1/1.1./${year} B`
      break
  }
  return value
}

export const formatConsecutive = (area, consecutiveTemp, total = 0, status, year, finalConsecutive) => {
  let value = ''
  let totalValue = 'XX'
  let consecutive = consecutiveTemp
  if (status === 'finish') {
    totalValue = total
    consecutive = finalConsecutive
  }
  switch (area) {
    case INTERNATIONALIZATION:
      value = `1/${year} ${consecutive} de ${totalValue}`
      break
    case CAPACITATION_AND_CERTIFICATIONS:
      value = `${consecutive} de ${totalValue}/${year}`
      break
    case COMPETITIVENESS:
      value = `001/${year} A LEGAJO ${consecutive} DE ${totalValue}`
      break
    case DIGITAL:
      value = `001/${year} B LEGAJO ${consecutive} DE ${totalValue}`
      break
  }
  return value
}

export const formatArchivenomyData = (area, company, buyer, modalityForBuyers, year) => {
  const archivenomy = {
    producingArea: "",
    fund: "",
    subfund: "",
    section: "",
    subsection: "",
    series: "",
    subseries: "",
    signature: "",
    placeFile: "",
    subject: "",
    subjectNotes: "",
    subjectNotes2: "",
    docValidity: "",
    procedureFile: "",
    concentrationFile: ""
  }
  switch (area) {
    case INTERNATIONALIZATION:
      let companyBuyers = ''
      let publish = ''
      if (company && company !== undefined) {
        companyBuyers = `EMPRESA ${company}`
      }
      if (buyer && buyer !== undefined) {
        companyBuyers = `COMPRADOR ${buyer}`
      }
      if (modalityForBuyers && modalityForBuyers === 4) {
        companyBuyers = `APOYO DE COFOCE A HOTEL POR ATRACCIÓN DE COMPRADORES`
      }
      if (year) {
        publish = parseInt(year) - 1
      }
      archivenomy.producingArea = 'DIRECCIÓN GENERAL DE PROMOCIÓN PARA LA EXPORTACIÓN'
      archivenomy.fund = 'COFOCE. Coordinadora de Fomento al Comercio Exterior'
      archivenomy.subfund = '0. NO APLICA'
      archivenomy.section = '3. DIRECCIÓN GENERAL DE PROMOCIÓN PARA LA EXPORTACIÓN'
      archivenomy.subsection = '2. COORDINACIÓN DE VINCULACIÓN DE SECTORES PRODUCTIVOS'
      archivenomy.series = '1. SOLICITUDES DE APOYO “IMPULSO A TU EMPRESA EN EL EXTRANJERO”'
      archivenomy.subseries = '0. NO APLICA'
      archivenomy.signature = ''
      archivenomy.placeFile = 'Plaza de la paz número 102, colonia puerto interior, piso 11, Silao, Guanajuato'
      archivenomy.subject = `CORRESPONDIENTE AL PROGRAMA “YA EXPORTO” PARA EL EJERCICIO FISCAL ${year} APROBADO EN REGLAS DE OPERACIÓN PUBLICADAS EN EL DIARIO OFICIAL DEL 31 DE DICIEMBRE DE ${publish}. ${companyBuyers}`
      archivenomy.docValidity = '6 años'
      archivenomy.procedureFile = '3 años'
      archivenomy.concentrationFile = '3 años'
      break
    case CAPACITATION_AND_CERTIFICATIONS:
      archivenomy.producingArea = ''
      archivenomy.fund = 'COFOCE. Coordinadora de Fomento al Comercio Exterior'
      archivenomy.subfund = '0. NO APLICA'
      archivenomy.section = '2. Dirección General de Capacitación y Certificaciones Internacionales'
      archivenomy.subsection = '3. Coordinación de Certificaciones Internacionales'
      archivenomy.series = '3. Gestión de Apoyos de Certificación'
      archivenomy.subseries = '0. NO APLICA'
      archivenomy.signature = ''
      archivenomy.placeFile = 'Plaza de la paz número 102, colonia puerto interior, piso 11, Silao, Guanajuato'
      archivenomy.subject = `EMPRESA ${company}`
      archivenomy.docValidity = '10 años'
      archivenomy.procedureFile = '5 años'
      archivenomy.concentrationFile = '5 años'
      break
    case COMPETITIVENESS:
      archivenomy.producingArea = 'DIRECCION GENERAL DE SERVICIOS DE APOYO AL COMERCIO EXTERIOR'
      archivenomy.fund = 'COFOCE. Coordinadora de Fomento al Comercio Exterior'
      archivenomy.subfund = '0. NO APLICA'
      archivenomy.section = '4. Dirección General de Servicios de Apoyo Al comercio exterior'
      archivenomy.subsection = '1. Dirección General de Comercio Exterior y Logística'
      archivenomy.series = '1. PROGRAMAS DE APOYOS'
      archivenomy.subseries = '1. APOYOS MULTISECTORIALES'
      archivenomy.signature = ''
      archivenomy.placeFile = 'Plaza de la paz número 102, colonia puerto interior, piso 11, Silao, Guanajuato'
      archivenomy.subject = `EXPEDIENTES DE APOYO A EMPRESA: ${company} DEL PROGRAMA COMERCIO & EXPORTACIÓN 4.0 COMPONENTE A. COMPETITIVIDAD`
      archivenomy.subjectNotes = `*Con base al Decreto 174 publicado en el Diario Oficial de la Federación el 15 de noviembre de 2016 en el Capítulo IV Organización Interna, fracción IV e inciso a).`
      archivenomy.subjectNotes2 = 'Realizamos la corrección de los nombres de la sección 4 que tiene por nombre Dirección General de Competitividad Internacional y de la subsección 1.'
      archivenomy.docValidity = '10 años'
      archivenomy.procedureFile = '3 años'
      archivenomy.concentrationFile = '7 años'
      break
    case DIGITAL:
      archivenomy.producingArea = 'DIRECCION GENERAL DE SERVICIOS DE APOYO AL COMERCIO EXTERIOR'
      archivenomy.fund = 'COFOCE. Coordinadora de Fomento al Comercio Exterior'
      archivenomy.subfund = '0. NO APLICA'
      archivenomy.section = '4. Dirección General de Servicios de Apoyo Al comercio exterior'
      archivenomy.subsection = '1. Dirección General de Comercio Exterior y Logística'
      archivenomy.series = '1. PROGRAMAS DE APOYOS'
      archivenomy.subseries = '1. APOYOS MULTISECTORIALES'
      archivenomy.signature = ''
      archivenomy.placeFile = 'Plaza de la paz número 102, colonia puerto interior, piso 11, Silao, Guanajuato'
      archivenomy.subject = `EXPEDIENTES DE APOYO A EMPRESA: ${company} DEL PROGRAMA COMERCIO & EXPORTACIÓN 4.0 COMPONENTE B. CROSSBORDER ECOMMERCE`
      archivenomy.subjectNotes = `*Con base al Decreto 174 publicado en el Diario Oficial de la Federación el 15 de noviembre de 2016 en el Capítulo IV Organización Interna, fracción IV e inciso a).`
      archivenomy.subjectNotes2 = 'Realizamos la corrección de los nombres de la sección 4 que tiene por nombre Dirección General de Competitividad Internacional y de la subsección 1.'
      archivenomy.docValidity = '10 años'
      archivenomy.procedureFile = '3 años'
      archivenomy.concentrationFile = '7 años'
      break
  }
  return archivenomy
}

export const formatClasificationReduce = (area, year) => {
  let value = ''
  switch (area) {
    case INTERNATIONALIZATION:
      value = `CFCE.0/3.2/1.0/1/${year}`
      break
    case CAPACITATION_AND_CERTIFICATIONS:
      value = `CFCE.0/2.3/3.0/${year}`
      break
    case COMPETITIVENESS:
      value = `CFCE.0/4.1/1.1/${year} A`
      break
    case DIGITAL:
      value = `CFCE.0/4.1/1.1./${year} B`
      break
  }
  return value
}

export const formatInventoryArchivenomyData = (area, year, company) => {
  const archivenomy = {
    archivalKey: "",
    description: "",
    subject: "",
    docValidity: "",
    procedureFile: "",
    concentrationFile: ""
  }
  switch (area) {
    case INTERNATIONALIZATION:
      archivenomy.archivalKey = formatClasificationReduce(area, year)
      archivenomy.description = `CORRESPONDIENTE AL PROGRAMA “YA EXPORTO” PARA EL EJERCICIO FISCAL ${year} APROBADO EN REGLAS DE OPERACIÓN PUBLICADAS EN EL DIARIO OFICIAL DEL 31 DE DICIEMBRE DE ${year - 1}. EMPRESA ${company}`
      archivenomy.subject = `CORRESPONDIENTE AL PROGRAMA “YA EXPORTO” PARA EL EJERCICIO FISCAL ${year} APROBADO EN REGLAS DE OPERACIÓN PUBLICADAS EN EL DIARIO OFICIAL DEL 31 DE DICIEMBRE DE ${year - 1}. EMPRESA ${company}`
      archivenomy.docValidity = '6 años'
      archivenomy.procedureFile = '3 años'
      archivenomy.concentrationFile = '3 años'
      break
    case CAPACITATION_AND_CERTIFICATIONS:
      archivenomy.archivalKey = formatClasificationReduce(area, year)
      archivenomy.description = `EMPRESA ${company}`
      archivenomy.subject = `EMPRESA ${company}`
      archivenomy.docValidity = '10 años'
      archivenomy.procedureFile = '5 años'
      archivenomy.concentrationFile = '5 años'
      break
    case COMPETITIVENESS:
      archivenomy.archivalKey = formatClasificationReduce(area, year)
      archivenomy.description = `EXPEDIENTES DE APOYO A EMPRESA: ${company} DEL PROGRAMA COMERCIO & EXPORTACIÓN 4.0 COMPONENTE A. COMPETITIVIDAD`
      archivenomy.subject = `EXPEDIENTES DE APOYO A EMPRESA: ${company} DEL PROGRAMA COMERCIO & EXPORTACIÓN 4.0 COMPONENTE A. COMPETITIVIDAD`
      archivenomy.docValidity = '10 años'
      archivenomy.procedureFile = '3 años'
      archivenomy.concentrationFile = '7 años'
      break
    case DIGITAL:
      archivenomy.archivalKey = formatClasificationReduce(area, year)
      archivenomy.description = `EXPEDIENTES DE APOYO A EMPRESA: ${company} DEL PROGRAMA COMERCIO & EXPORTACIÓN 4.0 COMPONENTE B. CROSSBORDER ECOMMERCE`
      archivenomy.subject = `EXPEDIENTES DE APOYO A EMPRESA: ${company} DEL PROGRAMA COMERCIO & EXPORTACIÓN 4.0 COMPONENTE B. CROSSBORDER ECOMMERCE`
      archivenomy.docValidity = '10 años'
      archivenomy.procedureFile = '3 años'
      archivenomy.concentrationFile = '7 años'
      break
  }
  return archivenomy
}

export const generateOpFolio = (id, created) => {
  const idString = id.toString()
  const year = created.substr(0, 4)
  const month = created.substr(5, 2)
  const day = created.substr(8, 2)
  const folio = `OP-${idString.padStart(4, '0')}-${year}${month}${day}`
  return folio
}

// FOR NUMERO LETRAS JS
/*
 * NumerosALetras.js
 * The MIT License (MIT)
 *
 * Copyright (c) 2016 Daniel M. Spiridione
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the 'Software'), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED 'AS IS', WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 * @author Daniel M. Spiridione (info@daniel-spiridione.com.ar)
 */
function unidades(num) {
  switch (num) {
    case 1:
      return 'Un'
    case 2:
      return 'Dos'
    case 3:
      return 'Tres'
    case 4:
      return 'Cuatro'
    case 5:
      return 'Cinco'
    case 6:
      return 'Seis'
    case 7:
      return 'Siete'
    case 8:
      return 'Ocho'
    case 9:
      return 'Nueve'
    default:
      return ''
  }
}

function decenasY(strSin, numUnidades) {
  if (numUnidades > 0) {
    return `${strSin} y ${unidades(numUnidades)}`
  }

  return strSin
}

function decenas(num) {
  const numDecena = Math.floor(num / 10)
  const numUnidad = num - (numDecena * 10)

  switch (numDecena) {
    case 1:
      switch (numUnidad) {
        case 0:
          return 'Diez'
        case 1:
          return 'Once'
        case 2:
          return 'Doce'
        case 3:
          return 'Trece'
        case 4:
          return 'Catorce'
        case 5:
          return 'Quince'
        default:
          return `Dieci${unidades(numUnidad).toLowerCase()}`
      }
    case 2:
      switch (numUnidad) {
        case 0:
          return 'Veinte'
        default:
          return `Veinti${unidades(numUnidad).toLowerCase()}`
      }
    case 3:
      return decenasY('Treinta', numUnidad)
    case 4:
      return decenasY('Cuarenta', numUnidad)
    case 5:
      return decenasY('Cincuenta', numUnidad)
    case 6:
      return decenasY('Sesenta', numUnidad)
    case 7:
      return decenasY('Setenta', numUnidad)
    case 8:
      return decenasY('Ochenta', numUnidad)
    case 9:
      return decenasY('Noventa', numUnidad)
    case 0:
      return unidades(numUnidad)
    default:
      return ''
  }
}

function centenas(num) {
  const numCentenas = Math.floor(num / 100)
  const numDecenas = num - (numCentenas * 100)

  switch (numCentenas) {
    case 1:
      if (numDecenas > 0) {
        return `Ciento ${decenas(numDecenas)}`
      }
      return 'Cien'
    case 2:
      return `Doscientos ${decenas(numDecenas)}`
    case 3:
      return `Trescientos ${decenas(numDecenas)}`
    case 4:
      return `Cuatrocientos ${decenas(numDecenas)}`
    case 5:
      return `Quinientos ${decenas(numDecenas)}`
    case 6:
      return `Seiscientos ${decenas(numDecenas)}`
    case 7:
      return `Setecientos ${decenas(numDecenas)}`
    case 8:
      return `Ochocientos ${decenas(numDecenas)}`
    case 9:
      return `Novecientos ${decenas(numDecenas)}`
    default:
      return decenas(numDecenas)
  }
}

function seccion(num, divisor, strSingular, strPlural) {
  const numCientos = Math.floor(num / divisor)
  const numResto = num - (numCientos * divisor)

  let letras = ''

  if (numCientos > 0) {
    if (numCientos > 1) {     
      letras = `${centenas(numCientos)} ${strPlural}`
    } else {
      letras = strSingular
    }
  }

  if (numResto > 0) {
    letras += ''
  }

  return letras
}

function miles(num) {
  const divisor = 1000
  const numCientos = Math.floor(num / divisor)
  const numResto = num - (numCientos * divisor)
  const strMiles = seccion(num, divisor, 'Un Mil', 'Mil')
  const strCentenas = centenas(numResto)

  if (strMiles === '') {
    return strCentenas
  }
  
  return (`${strMiles} ${strCentenas}`).trim()
}

function millones(num) {
  const divisor = 1000000
  const numCientos = Math.floor(num / divisor)
  const numResto = num - (numCientos * divisor)
  const strMillones = seccion(num, divisor, 'Un Millón de', 'Millones de')
  const strMiles = miles(numResto)

  if (strMillones === '') {
    return strMiles
  }

  return (`${strMillones} ${strMiles}`).trim()
}

export const NumerosALetras = (num, currency) => {
  const data = {
    numero: num,
    enteros: Math.floor(num),
    centavos: Math.round(num * 100) - (Math.floor(num) * 100),
    letrasCentavos: '',
    letrasMonedaPlural: currency.shortTextPlur,
    letrasMonedaSingular: currency.shortTextSing,
    letrasMonedaCentavoPlural: '/100',
    letrasMonedaCentavoSingular: '/100'
  }

  if (data.centavos >= 0) {
    data.letrasCentavos = function () {
      if (data.centavos >= 1 & data.centavos <= 9) {
        return `0 ${data.centavos}${data.letrasMonedaCentavoSingular}`
      }

      if (data.centavos === 0) {
        return `00 ${data.letrasMonedaCentavoSingular}`
      }

      return `${data.centavos}${data.letrasMonedaCentavoPlural}`
    }()
  }

  if (data.enteros === 0) {
    return (`Cero ${data.letrasMonedaPlural} ${data.letrasCentavos}`).trim()
  }

  if (data.enteros === 1) {
    return (`${millones(data.enteros)} ${data.letrasMonedaSingular} ${data.letrasCentavos}`).trim()
  }

  return (`${millones(data.enteros)} ${data.letrasMonedaPlural} ${data.letrasCentavos}`).trim()
}